import { createSlice } from '@reduxjs/toolkit';
import { SelectedTime, SelectedDate } from './Time.types';
import { timingKey } from '../../../../../../util/orderTiming';

type TimeState = {
  selectedTime: SelectedTime | null;
  selectedDate: SelectedDate | null;
  confirmedContext: string | null;
  explicitSelection: string | null;
  reviewRequired: boolean;
  checkoutContext: { id: string; key: string } | null;
};

export const INITIAL_STATE: TimeState = {
  selectedTime: null,
  selectedDate: null,
  confirmedContext: null,
  explicitSelection: null,
  reviewRequired: false,
  checkoutContext: null,
};

const mainSlice = createSlice({
  name: 'time',
  initialState: { ...INITIAL_STATE },
  reducers: {
    clearTimeConfirmation(state) {
      return { ...state, confirmedContext: null, explicitSelection: null };
    },
    setSelectedTime(state, action) {
      const previous = state.selectedTime;
      const next = action.payload;
      const previousWindow =
        typeof previous?.value === 'object' ? previous.value : null;
      const nextWindow = typeof next?.value === 'object' ? next.value : null;
      const unchanged =
        previous?.type === next?.type &&
        (next?.type === 'asap' ||
          (Number(previousWindow?.start) === Number(nextWindow?.start) &&
            Number(previousWindow?.end) === Number(nextWindow?.end)));
      return {
        ...state,
        selectedTime: action.payload,
        confirmedContext: unchanged ? state.confirmedContext : null,
        explicitSelection: unchanged ? state.explicitSelection : null,
        // An expired schedule may be reset to ASAP to load the menu. That
        // automatic reset is not a customer's acceptance of a different time.
        reviewRequired:
          state.reviewRequired ||
          (!unchanged && previous?.type === 'target' && next?.type === 'asap'),
      };
    },
    setSelectedDate(state, action) {
      return {
        ...state,
        selectedDate: action.payload,
      };
    },
    confirmSelectedTime(state, action) {
      const { restaurant, type, time } = action.payload;
      const key = timingKey(restaurant, type, time);
      if (
        key &&
        key === timingKey(restaurant, type, state.selectedTime) &&
        (time.type === 'asap' || Number(time.value?.end) > Date.now() / 1000)
      )
        return {
          ...state,
          confirmedContext: key,
          explicitSelection: timingKey('selection', type, time),
          reviewRequired: false,
        };
      return state;
    },
    // Picker submission records intent, not restaurant availability. In
    // particular, a marketplace choice has no restaurant context yet.
    acceptSelectedTime(state, action) {
      const { type, time } = action.payload;
      const key = timingKey('selection', type, time);
      if (
        key &&
        key === timingKey('selection', type, state.selectedTime) &&
        (time.type === 'asap' || Number(time.value?.end) > Date.now() / 1000)
      )
        return {
          ...state,
          explicitSelection: key,
          confirmedContext: null,
          reviewRequired: false,
        };
      return state;
    },
    bindCheckoutTime(state, action) {
      return { ...state, checkoutContext: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        ['order/setOrderType', 'restaurant/setSelectedRestaurantId'].includes(
          action.type
        ),
      (state, action) => {
        if (action.type === 'order/setOrderType' && state.explicitSelection) {
          // A real mode change invalidates intent even if the mode later
          // changes back. Restaurant navigation only revalidates availability.
          if (
            state.explicitSelection !==
            timingKey('selection', action.payload?.value, state.selectedTime)
          )
            return {
              ...state,
              confirmedContext: null,
              explicitSelection: null,
            };
        }
        if (!state.confirmedContext) return state;
        let restaurant;
        let type;
        try {
          [restaurant, type] = JSON.parse(state.confirmedContext);
        } catch (error) {
          return { ...state, confirmedContext: null, explicitSelection: null };
        }
        const changed =
          action.type === 'order/setOrderType'
            ? type !== action.payload?.value
            : restaurant !==
              String(action.payload?.profileKey ?? action.payload);
        return changed ? { ...state, confirmedContext: null } : state;
      }
    );
    builder.addMatcher(
      (action) =>
        ['order/resetOrder', 'order/checkTimeSet'].includes(action.type),
      (state, action) => {
        if (action.type === 'order/resetOrder' || action.payload === false)
          return {
            ...state,
            confirmedContext: null,
            explicitSelection:
              action.type === 'order/resetOrder' &&
              action.payload?.preserveTimeIntent
                ? state.explicitSelection
                : null,
            reviewRequired:
              action.type === 'order/resetOrder' &&
              !action.payload?.preserveTimeIntent
                ? false
                : state.reviewRequired,
            checkoutContext:
              action.type === 'order/resetOrder' ? null : state.checkoutContext,
          };
        return state;
      }
    );
  },
});

export const {
  setSelectedTime,
  setSelectedDate,
  confirmSelectedTime,
  acceptSelectedTime,
  bindCheckoutTime,
  clearTimeConfirmation,
} = mainSlice.actions;

export default mainSlice.reducer;
